import React from "react";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "@/components/ui";
import { ChevronDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BulkMessageAvatarCell } from "@/pages/BulkMessageOverview/BulkMessageAvatarCell";
import { User, BulkMessageSenderTypeEnum } from "@/__generated__/types";
import { useDataTableFilters } from "@/hooks/useDataTableFilters";

export interface FilterValue {
  value: string;
  user?: User;
  senderType?: BulkMessageSenderTypeEnum;
  renderComponent?: React.ComponentType<{
    value: string;
    user?: User;
    senderType?: BulkMessageSenderTypeEnum;
  }>;
}

export interface CustomFilterTable {
  id: string;
  label: string;
  values: FilterValue[];
  avatarFilterLabel?: boolean;
}

interface FilterTableComponentProps {
  customFilters?: CustomFilterTable[];
  onFilterChange?: (filterId: string, values: string[]) => void;
  displayValue?: string | React.ReactNode;
}

export function FilterTableComponent({
  customFilters,
  onFilterChange,
  displayValue,
}: FilterTableComponentProps) {
  const { t } = useTranslation();
  const {
    filtersToRender,
    handleFilterChange: handleInternalFilterChange,
    handleResetFilters,
    currentFilter,
    disabled,
  } = useDataTableFilters(customFilters);

  const handleFilterChange = (columnId: string, value: string): void => {
    handleInternalFilterChange(columnId, value);
    if (onFilterChange) {
      onFilterChange(columnId, [value]);
    }
  };

  const renderFilterValue = (
    filter: CustomFilterTable,
    { value, user, senderType, renderComponent }: FilterValue,
  ) => {
    if (renderComponent) {
      const Component = renderComponent;
      return <Component value={value} user={user} senderType={senderType} />;
    }

    if (filter.avatarFilterLabel && user) {
      return (
        <BulkMessageAvatarCell
          users={[user] as User[]}
          senderType={senderType as BulkMessageSenderTypeEnum}
        />
      );
    }

    return value;
  };

  return (
    <Menubar
      role="menubar"
      aria-label={t("dataTable.tableFilters")}
      className={`h-8 ${
        disabled
          ? "pointer-events-none cursor-not-allowed text-icon opacity-50"
          : "cursor-pointer hover:bg-background-light"
      }`}
    >
      <MenubarMenu>
        {currentFilter ? (
          <div
            className="flex w-max items-center px-3 font-medium"
            role="menuitem"
            aria-live="polite"
            aria-label={t("dataTable.currentFilter")}
          >
            <span className="text-sm">
              {filtersToRender.find((f) => f.id === currentFilter.id)?.label}
              {": "}
              {displayValue ?? String(currentFilter.value)}
            </span>
            <span
              className="ml-2 cursor-pointer text-primary-light"
              onClick={() => {
                handleResetFilters();
                if (onFilterChange) {
                  onFilterChange(currentFilter.id, []);
                }
              }}
              role="button"
              aria-label={t("dataTable.clearFilters")}
              tabIndex={0}
            >
              <X className="h-4 w-4 text-icon" />
            </span>
          </div>
        ) : (
          <MenubarTrigger
            disabled={disabled}
            role="menuitem"
            aria-haspopup="true"
            data-testid="filters-trigger"
            aria-label={t("dataTable.openFiltersMenu")}
            aria-disabled={disabled}
            className="flex items-center gap-2 text-sm"
          >
            {t("global.filter")}
            <ChevronDown
              className="ml-auto h-4 w-4 text-sm transition-transform group-data-[state=open]/collapsible:rotate-180"
              aria-hidden="true"
            />
          </MenubarTrigger>
        )}

        <MenubarContent
          data-testid="filters-container"
          aria-label={t("dataTable.filtersContainer")}
          role="menu"
          aria-orientation="vertical"
        >
          {filtersToRender.map((filter) => (
            <MenubarSub key={filter.id}>
              <MenubarSubTrigger
                role="menuitem"
                data-testid={`filter-by-${String(filter.label).replace(/\s/g, "-")}`}
              >
                {filter.label}
              </MenubarSubTrigger>
              <MenubarSubContent
                role="menu"
                aria-label={t("dataTable.filterBy", { column: filter.label })}
              >
                {filter.values.map(
                  (value, index) =>
                    value.value && (
                      <MenubarItem
                        key={`${value.value}-${index}`}
                        onClick={() =>
                          handleFilterChange(filter.id, value.value)
                        }
                        role="menuitem"
                        data-testid={`filter-by-${String(filter.label).replace(/\s/g, "-")}-${String(value.value).replace(/\s/g, "-")}`}
                        aria-label={t("dataTable.filterByValue", {
                          item: value.value,
                          column: filter.label,
                        })}
                      >
                        {renderFilterValue(filter, value)}
                      </MenubarItem>
                    ),
                )}
              </MenubarSubContent>
            </MenubarSub>
          ))}
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  );
}
